import React, { CSSProperties, useState } from 'react'
import { List, Modal, Row, Tooltip } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ApplicationState } from 'src/store'
import { ButtonTitled, Empty, Text } from 'src/components/Ant'
import { Request, RequestTicket } from 'src/types'
import { saveRequestTickets } from 'src/core/store/ducks/requests/actions'
import { SyncStatusBadge } from 'src/components/atoms/sync-status-badge'
import { TagTicketSituation } from 'src/components/molecules'
import currencyMask from 'src/components/Formik/InputCurrency/mask'
import UserAvatar from 'src/components/UserAvatar'
import ModalForm from '../ModalForm'
import { Card, SpanFilled } from './styles'

interface Props {
  bodyStyle?: CSSProperties
  enableModal?: boolean
  handleDelete?(item: RequestTicket): void
  handleEdit?(item: RequestTicket): void
  request: Request
}

export default function AdvancesSummary({
  bodyStyle = {},
  enableModal = false,
  handleDelete,
  handleEdit,
  request
}: Props) {
  const {
    me: {
      user: { role_group, tenant_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [visibleModal, setVisibleModal] = useState<boolean>(false)
  const [itemEdit, setItemEdit] = useState<RequestTicket>()

  function openEditModal(item: RequestTicket) {
    setVisibleModal(true)
    setItemEdit(item)
  }

  function confirmDelete(item: RequestTicket) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('common.actions.delete.message', {
        number: `#${item.request_ticket_number ?? ''}`
      }),
      onCancel() {},
      onOk: async () => {
        return new Promise(() => {
          dispatch(saveRequestTickets([{ ...item, active: false }]))
        })
      },
      title: t('common.actions.delete.title')
    })
  }

  const normalEdit =
    enableModal &&
    !['CONFERENCE_REPROVED', 'CLOSING_REPROVED'].includes(request.status)

  const tenant559042084949Edit =
    Boolean(role_group.approver.length || role_group.checker.length) && //is Approver or Checker
    +tenant_id === 559042084949 && // is tenant
    !['FINISHED', 'CANCELED'].includes(request.status) && // is not disabled
    typeof request.request_id === 'number' // is not new request

  const canChangeAdvance = normalEdit || tenant559042084949Edit

  return (
    <>
      {canChangeAdvance && (
        <ModalForm
          itemEdit={itemEdit}
          request={request}
          toggle={() => {
            setVisibleModal(false)
            setItemEdit(null)
          }}
          visible={visibleModal}
        />
      )}
      <Card
        data-test-id="summary-advances"
        bodyStyle={bodyStyle}
        title="web.services.advances.list-title"
        extra={[
          ...(canChangeAdvance
            ? [
                <ButtonTitled
                  icon="plus"
                  key="add-advance"
                  onClick={() => setVisibleModal(true)}
                  size="default"
                  tooltip="common.actions.add"
                />
              ]
            : [])
        ]}
      >
        <List
          data-test-id="advance-list"
          dataSource={request.request_tickets.filter(
            ({ request_ticket_title_advances, request_ticket_type }) =>
              request_ticket_type === 'ADVANCE' &&
              request_ticket_title_advances.length
          )}
          itemLayout="horizontal"
          locale={{ emptyText: <Empty onlyText /> }}
          renderItem={(item: RequestTicket, index: number) => (
            <List.Item
              actions={[
                ...(!!handleEdit || canChangeAdvance
                  ? [
                      <ButtonTitled
                        icon="edit"
                        onClick={
                          canChangeAdvance
                            ? () => openEditModal(item)
                            : () => handleEdit(item)
                        }
                        shape="circle"
                        tooltip="common.actions.edit"
                      />
                    ]
                  : []),
                ...(!!handleDelete || canChangeAdvance
                  ? [
                      <ButtonTitled
                        icon="delete"
                        onClick={
                          canChangeAdvance
                            ? () => confirmDelete(item)
                            : () => handleDelete(item)
                        }
                        shape="circle"
                        tooltip="common.actions.remove"
                        type="danger"
                      />
                    ]
                  : [])
              ]}
              data-test-id={`advance-item-${index}`}
            >
              <List.Item.Meta
                avatar={
                  <Tooltip
                    placement="top"
                    title={item.request_ticket_user_data[0].traveler.name}
                  >
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a>
                      <UserAvatar
                        gender={
                          item.request_ticket_user_data[0].traveler.gender
                        }
                        name={item.request_ticket_user_data[0].traveler.name}
                      />
                    </a>
                  </Tooltip>
                }
                title={
                  !!item.request_ticket_title_advances[0].situation && (
                    <>
                      <SpanFilled>{`#${
                        item.request_ticket_number ?? ''
                      }`}</SpanFilled>
                      <SyncStatusBadge status={item?.sync_status} />
                      <TagTicketSituation
                        status={item.request_ticket_title_advances[0].situation}
                      />
                    </>
                  )
                }
                description={
                  <>
                    <Row>
                      <Text
                        strong
                        title="web.services.advances.form.currency.title"
                      />
                      {': '}
                      {item.request_ticket_title_advances[0].currency.name}
                    </Row>
                    <Row>
                      <Text
                        strong
                        title="web.services.advances.form.amount.title"
                      >
                        {': '}
                      </Text>
                      {
                        currencyMask({
                          formatStr: true,
                          value: item.request_ticket_title_advances[0].amount
                        }).maskedValue
                      }
                    </Row>
                    {item.request_ticket_title_advances[0].currency.code !==
                      'BRL' && (
                      <Row>
                        <Text
                          strong
                          title="web.services.advances.form.exchange-rate.title"
                        >
                          {': '}
                        </Text>
                        {
                          currencyMask({
                            formatStr: true,
                            precision: 4,
                            value:
                              item.request_ticket_title_advances[0]
                                .exchange_rate
                          }).maskedValue
                        }
                      </Row>
                    )}
                    <Row>
                      <Text
                        strong
                        title="web.services.advances.form.resource.title"
                      >
                        {': '}
                      </Text>
                      {
                        item.request_ticket_title_advances[0].financial_resource
                          .name
                      }
                    </Row>
                  </>
                }
              />
            </List.Item>
          )}
        />
      </Card>
    </>
  )
}
